/**
 * Clutch shared contracts — the single source of truth for every workstream.
 * Change only through the integrator (see SPEC.md §5). Zod schemas guard every
 * boundary where untrusted input enters (HTTP bodies, fixtures, model files).
 */
import { z } from 'zod';
import type { DirectorProfile } from './director-contracts.js';

// ───────────────────────────── Game data ─────────────────────────────

export type Side = 'home' | 'away';

export const EVENT_TYPES = [
  'period_start',
  'period_end',
  'game_end',
  'shot',
  'freethrow',
  'rebound',
  'turnover',
  'steal',
  'block',
  'foul',
  'timeout',
  'substitution',
  'jumpball',
  'violation',
  'other',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** One normalized play-by-play action. Scores are the totals *after* the action. */
export const GameEventSchema = z.object({
  eventId: z.string(), // `${gameId}:${actionNumber}`
  gameId: z.string(),
  seq: z.number().int().nonnegative(), // 0-based order within the game
  period: z.number().int().positive(), // 1–4 regulation, 5+ overtime
  clockSec: z.number().nonnegative(), // seconds remaining in the period
  wallClock: z.string(), // ISO-8601 UTC time the action happened (real pacing)
  type: z.enum(EVENT_TYPES),
  side: z.enum(['home', 'away']).optional(), // team credited with the action
  personId: z.number().int().optional(),
  playerName: z.string().optional(),
  made: z.boolean().optional(),
  points: z.number().int().min(0).max(3).optional(), // points scored on this action
  playerPoints: z.number().int().optional(), // player's running total after the action
  x: z.number().optional(), // court coordinates (0–100 feed units), shots only
  y: z.number().optional(),
  possession: z.enum(['home', 'away']).optional(), // team in possession after the action
  homeScore: z.number().int().nonnegative(),
  awayScore: z.number().int().nonnegative(),
  description: z.string(),
});
export type GameEvent = z.infer<typeof GameEventSchema>;

export const TeamRefSchema = z.object({
  teamId: z.number().int(),
  tricode: z.string().length(3),
});
export type TeamRef = z.infer<typeof TeamRefSchema>;

export const GameMetaSchema = z.object({
  gameId: z.string(),
  date: z.string(), // YYYY-MM-DD (US Eastern game date approximated from tip-off UTC)
  tipoffUtc: z.string(),
  home: TeamRefSchema,
  away: TeamRefSchema,
  finalHome: z.number().int(),
  finalAway: z.number().int(),
  periods: z.number().int().positive(),
  /** For 'espn_public' games: teamId is ESPN's (use nbaTeamId(tricode) for NBA assets), and finalHome/finalAway hold the current score until game_end. */
  source: z.enum(['public_research_archive', 'espn_public', 'official_licensed', 'fixture']),
});
export type GameMeta = z.infer<typeof GameMetaSchema>;

export const GameFixtureSchema = z.object({
  meta: GameMetaSchema,
  events: z.array(GameEventSchema),
});
export type GameFixture = z.infer<typeof GameFixtureSchema>;

export type PlayerLine = { personId: number; name: string; side: Side; points: number };

export type PlaySummary = {
  eventId: string;
  seq: number;
  period: number;
  clockSec: number;
  side?: Side;
  text: string;
  points?: number;
};

/** Game state as of a given event. Produced only by `reduce` in state.ts. */
export type GameState = {
  gameId: string;
  status: 'pre' | 'live' | 'final';
  asOfSeq: number; // -1 before the first event
  period: number;
  clockSec: number;
  homeScore: number;
  awayScore: number;
  possession?: Side;
  leadChanges: number;
  lastLeader?: Side; // last side to hold the lead (ties don't reset it)
  maxDeficit: { home: number; away: number }; // largest deficit each side has faced
  players: Record<number, PlayerLine>;
  lastPlays: PlaySummary[]; // most recent first, meaningful plays only, max 5
};

// ───────────────────────────── Intelligence ─────────────────────────────

/** Versioned logistic-regression win-probability model (trained in apps/lab). */
export const WinProbModelSchema = z.object({
  version: z.string(),
  trainedAt: z.string(),
  features: z.array(z.string()),
  intercept: z.number(),
  coefficients: z.array(z.number()),
  metrics: z.record(z.string(), z.number()), // e.g. brier, logloss, ece on held-out games
  trainGames: z.number().int(),
  testGames: z.number().int(),
});
export type WinProbModel = z.infer<typeof WinProbModelSchema>;

export type ProbabilityPoint = {
  seq: number;
  eventId: string;
  period: number;
  clockSec: number;
  pHome: number; // P(home team wins | state)
  modelVersion: string;
};

export type MomentDecision = {
  momentId: string;
  gameId: string;
  eventId: string;
  seq: number;
  period: number;
  clockSec: number;
  score: number; // 0–1, higher = more likely to become a worthy moment
  isMoment: boolean;
  reasons: string[]; // human-readable, e.g. "3-point game, 2:18 left in Q4"
  detectorVersion: string;
};

// ───────────────────────────── Fans ─────────────────────────────

export const ALERT_BUDGETS = [1, 3, 7] as const; // invites per week
export const FanInputSchema = z.object({
  displayName: z.string().trim().min(1).max(24),
  team: z.string().length(3).optional(), // tricode
  player: z.string().max(80).optional(),
  budgetPerWeek: z.union([z.literal(1), z.literal(3), z.literal(7)]).default(3),
  timeZone: z.string().default('America/New_York'),
  quietHours: z.tuple([z.number().int().min(0).max(23), z.number().int().min(0).max(23)]).optional(),
});
export type FanInput = z.infer<typeof FanInputSchema>;
export type Fan = FanInput & {
  fanId: string;
  createdAt: string;
  /** Canonical preferences persisted with the identity; legacy separate profiles migrate on save. */
  directorProfile?: DirectorProfile;
  directorAnswered?: string[];
};

export type Invite = {
  /** Missing on older clients/events, which are treated as replay invites. */
  mode?: 'live' | 'replay';
  inviteId: string;
  fanId: string;
  roomId: string;
  gameId: string;
  momentId: string;
  headline: string; // ≤ 120 chars, built only from `facts`
  facts: {
    matchup: string; // "NYK @ BOS"
    score: string; // "104–108"
    clock: string; // "Q4 2:18"
    playerLine?: string; // "Brunson has 36"
    friendCalls: { displayName: string; label: string }[];
  };
  at: string;
};

// ───────────────────────────── Rooms & calls ─────────────────────────────

export const REACTIONS = ['🔥', '😱', '🧊', '💀', '🙏', '👀'] as const;
export type ReactionEmoji = (typeof REACTIONS)[number];

export type Choice = 'yes' | 'no';

export type CallOffer = {
  callId: string;
  roomId: string;
  gameId: string;
  kind: 'game_winner';
  subject: Side; // the side the question is about ("Knicks come back and win?")
  question: string;
  yesLabel: string; // "Knicks"
  noLabel: string; // "Celtics"
  offeredAtSeq: number;
  offeredAt: { period: number; clockSec: number };
  cutoff: { period: number; clockSec: number }; // no locks once the game passes this point
  pYesAtOffer: number;
  modelVersion: string;
  status: 'open' | 'closed' | 'resolved' | 'void';
  /** Who opened it: the moment detector, or a fan challenging the room (same resolver, same model). */
  openedBy?: { kind: 'detector' } | { kind: 'fan'; fanId: string; displayName: string };
};

/** A fan's own camera/voice reaction, anchored to the game moments it covers. */
export type Take = {
  takeId: string;
  roomId: string;
  gameId: string;
  fanId: string;
  displayName: string;
  mimeType: string; // e.g. video/webm
  durationMs: number;
  startedAtSeq: number; // game seq when recording started
  endedAtSeq: number;
  /** Game events (seq) and their offset into the recording, captured by the client as events arrived. */
  marks: { seq: number; offsetMs: number }[];
  url: string; // served by the API: /rooms/:id/takes/:takeId/media
  createdAt: string;
};
export const TakeMetaSchema = z.object({
  fanId: z.string(),
  mimeType: z.string().regex(/^(video|audio)\/[\w.+-]+(;.*)?$/),
  durationMs: z.number().int().positive().max(120_000),
  startedAtSeq: z.number().int().min(-1),
  endedAtSeq: z.number().int().min(-1),
  marks: z.array(z.object({ seq: z.number().int(), offsetMs: z.number().int().nonnegative() })).max(2000),
});

/** A game Clutch can open a room on right now (live feed) — distinct from committed replays. */
export type LiveGame = {
  gameId: string; // source-prefixed, e.g. "espn:401898404"
  source: 'espn_public';
  shortName: string; // "BKN @ MIA"
  status: 'pre' | 'live' | 'final';
  detail: string; // "Q3 4:12" / "7:30 PM EDT" / "Final"
  startUtc: string;
  home: TeamRef & { score: number };
  away: TeamRef & { score: number };
};

/** Official, embeddable video for a game (e.g. the NBA's YouTube highlights), curated per game. */
export const GameMediaSchema = z.object({
  gameId: z.string(),
  provider: z.literal('youtube'),
  videoId: z.string().regex(/^[\w-]{6,20}$/),
  title: z.string(),
  /** Optional: where in the video each key play appears (seconds), curated by a person watching it. */
  clips: z.array(z.object({ eventId: z.string(), start: z.number().nonnegative(), end: z.number().positive() })).default([]),
});
export type GameMedia = z.infer<typeof GameMediaSchema>;

export const LockRequestSchema = z.object({
  fanId: z.string(),
  choice: z.enum(['yes', 'no']),
  idempotencyKey: z.string().min(8).max(64),
});
export type LockRequest = z.infer<typeof LockRequestSchema>;

export type CallLock = {
  callId: string;
  fanId: string;
  displayName: string;
  choice: Choice;
  idempotencyKey: string;
  status: 'confirmed' | 'rejected';
  reason?: 'after_cutoff' | 'already_locked' | 'unknown_call' | 'not_in_room';
  lockedAtSeq: number; // last game event applied when the lock was accepted
  lockedAt: { period: number; clockSec: number };
  pChoiceAtLock: number; // model probability of the side the fan chose
  serverLockedAt: string;
};

export type Resolution = {
  callId: string;
  outcome: Choice | 'void';
  finalHome: number;
  finalAway: number;
  decidedByEventId: string;
  results: { fanId: string; displayName: string; choice: Choice; correct: boolean }[];
  resolverVersion: string;
};

export type Receipt = {
  receiptId: string;
  roomId: string;
  callId: string;
  fanId: string;
  displayName: string;
  gameId: string;
  matchup: string;
  question: string;
  choice: Choice;
  choiceLabel: string;
  lockedAt: { period: number; clockSec: number };
  pChoiceAtLock: number;
  result: 'correct' | 'incorrect' | 'void';
  finalScore: string; // "NYK 112 – BOS 114"
  roomCalls: { displayName: string; choiceLabel: string; correct: boolean }[];
  modelVersion: string;
  resolverVersion: string;
};

/** A reaction is anchored to the game moment it answers: the last applied play. */
export type Reaction = {
  fanId: string;
  displayName: string;
  emoji: ReactionEmoji;
  atSeq: number; // game event seq applied when the reaction arrived (-1 before tip-off)
  playEventId?: string; // most recent meaningful play (state.lastPlays[0]) at that moment
};

/** "What you missed": short catch-up for a fan joining mid-game, built only from real events. */
export type RewindBeat = {
  kind: 'deficit' | 'run' | 'lead_change' | 'star' | 'friends' | 'now';
  text: string; // e.g. "Hawks were down 14 in the third."
  seq?: number; // the event the beat points at, for highlighting on the court
  period: number;
  clockSec: number;
  side?: Side;
};
export type Rewind = { roomId: string; gameId: string; asOfSeq: number; beats: RewindBeat[] };

export type Presence = { fanId: string; displayName: string; team?: string; online: boolean };

/** Live-feed health, so the UI can say exactly where data comes from and how fresh it is. */
export type FeedStatus = {
  mode: 'replay' | 'live';
  source: GameMeta['source'];
  lastPollUtc?: string;
  lastEventUtc?: string; // wall clock of the newest event
  lagMs?: number; // receipt time − event time for the newest event
  healthy: boolean;
  message?: string;
};

export type ReplayStatus = {
  status: 'idle' | 'playing' | 'paused' | 'finished';
  speed: number; // 1 = real pacing from wall-clock timestamps
  cursorSeq: number; // last applied event seq (-1 before start)
  totalEvents: number;
};

export type RoomSnapshot = {
  roomId: string;
  gameId: string;
  meta: GameMeta;
  replay: ReplayStatus;
  state: GameState;
  probability: ProbabilityPoint[]; // full history so far
  moments: MomentDecision[]; // moments that fired (isMoment) so far
  presence: Presence[];
  call?: CallOffer;
  locks: CallLock[]; // confirmed only
  resolution?: Resolution;
  /** Present for live rooms: feed health on connect, before the first `feed` event. */
  feed?: FeedStatus;
};

/** Read-only Home context from an already tracked public game community. */
export type LiveGameContext = {
  gameId: string;
  roomId: string;
  meta: GameMeta;
  state: GameState;
  /** Last 40 applied events, chronological. This is a partial history. */
  plays: GameEvent[];
  /** False when the window contains a known, unreconciled source correction. */
  historyReliable: boolean;
  /** Most recent meaningful play whose content still agrees with the source. */
  latestPlay?: GameEvent;
  feed: FeedStatus;
  observedAt: string;
  freshness: 'current' | 'connecting' | 'stale' | 'error';
  /** Connected profiles, not historical members; labelled test profiles are a subset. */
  onlineCount: number;
  testOnlineCount: number;
  publishedTakeCount: number;
  testPublishedTakeCount: number;
  /** Published metadata only: no automatic camera/video exposure on Home. */
  latestTake?: Pick<Take, 'takeId' | 'fanId' | 'displayName' | 'createdAt'> & { isTest: boolean };
  recentReactions: (Reaction & { at: string; isTest: boolean })[];
};

export type LiveContextResponse = { generatedAt: string; contexts: LiveGameContext[] };

/** GET /rooms list item. */
export type RoomSummary = {
  roomId: string;
  gameId: string;
  matchup: string;
  createdAt: string;
  replay: ReplayStatus;
  mode: 'replay' | 'live';
  feed?: FeedStatus;
};

type RoomEventBase = { roomId: string; seq: number; at: string };
export type RoomEvent = RoomEventBase &
  (
    | { kind: 'snapshot'; data: RoomSnapshot }
    | { kind: 'game_event'; data: { event: GameEvent; state: GameState } }
    | { kind: 'probability'; data: ProbabilityPoint }
    | { kind: 'moment'; data: MomentDecision }
    | { kind: 'call_offer'; data: CallOffer }
    | { kind: 'call_lock'; data: CallLock }
    | { kind: 'call_closed'; data: { callId: string } }
    | { kind: 'presence'; data: Presence[] }
    | { kind: 'reaction'; data: Reaction }
    | { kind: 'resolution'; data: Resolution }
    | { kind: 'replay'; data: ReplayStatus }
    | { kind: 'take'; data: Take }
    | { kind: 'take_removed'; data: { takeId: string } }
    | { kind: 'feed'; data: FeedStatus }
  );
export type RoomEventKind = RoomEvent['kind'];

/** Events on a fan's personal stream (home screen). */
export type FanEvent =
  | { kind: 'invite'; data: Invite }
  | { kind: 'invite_update'; data: { inviteId: string; friendCalls: Invite['facts']['friendCalls'] } };

// ───────────────────────────── HTTP bodies ─────────────────────────────

export const CreateRoomSchema = z.object({
  gameId: z.string(),
  /** Replay start; omitted = from the tip (archive games) or late in Q4 (finished ESPN games). */
  startAtSeq: z.number().int().nonnegative().optional(),
  speed: z.number().positive().max(120).default(1),
  /** 'recap' explicitly opens the complete ending of a verified finished game. Normal replays retain their own cursor. */
  mode: z.enum(['live', 'replay', 'recap']).optional(),
  /** Join the shared live game room. Replay rooms remain independently paced. */
  community: z.boolean().optional(),
});
export const JoinRoomSchema = z.object({ fanId: z.string() });
export const ReactionSchema = z.object({ fanId: z.string(), emoji: z.enum(REACTIONS) });
export const ControlSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('play') }),
  z.object({ action: z.literal('pause') }),
  z.object({ action: z.literal('speed'), speed: z.number().positive().max(120) }),
  z.object({ action: z.literal('seek'), seq: z.number().int().nonnegative() }),
]);

// ───────────────────────────── Intelligence boundary ─────────────────────────────

/**
 * What the server needs from the intelligence layer. Implemented by
 * `createIntelligence(model)` in packages/core/src/intelligence (intelligence workstream);
 * injected into the room engine so server tests can use deterministic doubles.
 */
export interface Intelligence {
  readonly modelVersion: string;
  readonly detectorVersion: string;
  /** P(home wins | state). Must use only `state` (no lookahead). */
  pHome(state: GameState): number;
  /** Stateful per-game detector; call once per applied event, in order. */
  createDetector(meta: GameMeta): {
    observe(state: GameState, point: ProbabilityPoint, event: GameEvent): MomentDecision;
  };
  /** Should this fan be invited to this moment? Pure; `invitesLast7d` counts prior invites. */
  inviteDecision(input: {
    fan: Fan;
    meta: GameMeta;
    state: GameState;
    moment: MomentDecision;
    invitesLast7d: number;
    nowUtc: string;
  }): { invite: boolean; reason: string; relevance: number };
}
